import React, { useEffect, useMemo, useState } from "react";
import io from "socket.io-client";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  PlusOutlined,
  SaveOutlined,
  ScissorOutlined,
  UploadOutlined,
  CloseOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { API_BASE, SOCKET_URL } from "../../config/api";
import { getAuthHeaders } from "../../utils/auth";
import CreateCategory from "./CreateCategory";
import CreateSubCategory from "./CreateSubCategory";
import CreateService from "./CreateService";

const { Title, Text } = Typography;

const sectionCardStyle = {
  borderRadius: 22,
  border: "1px solid #eef2f6",
  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.05)",
};

const metricCardStyle = {
  borderRadius: 18,
  border: "1px solid #f1f5f9",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

const nestedTableClassName = "catalog-nested-table";

const AdminServices = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState("");
  const [editForm, setEditForm] = useState({});
  const [fileList, setFileList] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [isSubModalOpen, setIsSubModalOpen] = useState(false);
  const [isSrvModalOpen, setIsSrvModalOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/category`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Error al cargar los datos");
      }

      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("fetchData admin servicios:", error);
      setCategories([]);
      message.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleCatalogUpdate = () => {
      fetchData();
    };

    const handleEmployeesUpdate = () => {
      fetchData();
    };

    socket.on("catalog:updated", handleCatalogUpdate);
    socket.on("employees:updated", handleEmployeesUpdate);

    return () => {
      socket.off("catalog:updated", handleCatalogUpdate);
      socket.off("employees:updated", handleEmployeesUpdate);
      socket.disconnect();
    };
  }, []);

  const stats = useMemo(() => {
    const subCategoriesCount = categories.reduce(
      (total, category) => total + (category.SubCategories?.length || 0),
      0
    );
    const servicesCount = categories.reduce(
      (total, category) =>
        total +
        (category.SubCategories || []).reduce(
          (subTotal, subCategory) => subTotal + (subCategory.Services?.length || 0),
          0
        ),
      0
    );

    return {
      categories: categories.length,
      subCategories: subCategoriesCount,
      services: servicesCount,
    };
  }, [categories]);

  const save = async (id, type) => {
    setIsUpdating(true);
    try {
      const cleanId = id.toString().split("-").pop();
      const endpoints = {
        category: "category",
        subcategory: "subcategory",
        service: "services",
      };

      let response;
      const url = `${API_BASE}/${endpoints[type]}/${cleanId}`;

      if (type === "category") {
        const formData = new FormData();
        formData.append("name", editForm.name);
        if (fileList.length > 0) {
          formData.append("image", fileList[0].originFileObj);
        }

        response = await fetch(url, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: formData,
        });
      } else {
        const payload = {
          name: editForm.name,
          priceTotal: editForm.priceTotal,
          duration: editForm.duration,
        };

        if (type === "service") {
          payload.price = editForm.price;
          payload.priceTotal = editForm.priceTotal;
          payload.duration = editForm.duration;
          payload.combinable = editForm.combinable;
        }

        if (type === "subcategory") {
          payload.categoryId = editForm.categoryId;
        }

        response = await fetch(url, {
          method: "PUT",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        message.success("Actualizado correctamente");
        setEditingKey("");
        fetchData();
      } else {
        const errorData = await response.json();
        message.error(errorData.message || "Error al actualizar");
      }
    } catch (err) {
      console.error("Error en save:", err);
      message.error("Error de conexión al guardar");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id, type) => {
    try {
      const cleanId = id.toString().split("-").pop();
      const endpoints = {
        category: "category",
        subcategory: "subcategory",
        service: "services",
      };

      const url = `${API_BASE}/${endpoints[type]}/${cleanId}`;

      const res = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        message.success(
          `${type.charAt(0).toUpperCase() + type.slice(1)} eliminada con éxito`
        );
        fetchData();
      } else {
        const errorData = await res.json();
        message.error(errorData.message || "No se pudo eliminar el registro");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error de conexión con el servidor");
    }
  };

  const startEdit = (record) => {
    setEditingKey(record.id);
    setEditForm({
      ...record,
      combinable:
        typeof record.combinable === "boolean" ? record.combinable : false,
    });
    setFileList([]);
  };

  const renderActions = (record, type) => {
    const editable = editingKey === record.id;

    return editable ? (
      <Space>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          size="small"
          onClick={() => save(record.id, type)}
          loading={isUpdating}
        >
          Guardar
        </Button>
        <Button
          size="small"
          icon={<CloseOutlined />}
          onClick={() => setEditingKey("")}
        >
          Cancelar
        </Button>
      </Space>
    ) : (
      <Space size="middle">
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => startEdit(record)}
          disabled={editingKey !== ""}
          style={{ color: "#3b82f6" }}
        />
        <Popconfirm
          title={`¿Eliminar ${type === "service" ? "servicio" : type}?`}
          onConfirm={() => handleDelete(record.id, type)}
          okText="Sí"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            disabled={editingKey !== ""}
          />
        </Popconfirm>
      </Space>
    );
  };

  const categoryColumns = [
    {
      title: "Imagen",
      dataIndex: "image",
      key: "image",
      width: 120,
      render: (img, record) => {
        if (editingKey === record.id) {
          return (
            <Upload
              beforeUpload={() => false}
              listType="picture-card"
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList: nextFiles }) => setFileList(nextFiles)}
            >
              {fileList.length < 1 && <UploadOutlined />}
            </Upload>
          );
        }

        return img ? (
          <Avatar
            src={img}
            shape="square"
            size={58}
            icon={<PictureOutlined />}
            style={{ borderRadius: 14 }}
          />
        ) : (
          <Avatar
            shape="square"
            size={58}
            icon={<PictureOutlined />}
            style={{ borderRadius: 14, background: "#eff6ff", color: "#3b82f6" }}
          />
        );
      },
    },
    {
      title: "Área del salón",
      dataIndex: "name",
      key: "name",
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
        ) : (
          <Text strong style={{ fontSize: 15 }}>
            {text}
          </Text>
        ),
    },
    {
      title: "Acciones",
      key: "action",
      width: 170,
      render: (_, record) => renderActions(record, "category"),
    },
  ];

  const subCategoryColumns = [
    {
      title: "Tipo de servicio",
      dataIndex: "name",
      key: "name",
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
        ) : (
          <Text>{text}</Text>
        ),
    },
    {
      title: "Acciones",
      key: "action",
      width: 170,
      render: (_, record) => renderActions(record, "subcategory"),
    },
  ];

  const serviceColumns = [
    {
      title: "Servicio",
      dataIndex: "name",
      key: "name",
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={editForm.name}
            onChange={(e) =>
              setEditForm({
                ...editForm,
                name: e.target.value,
              })
            }
          />
        ) : (
          <Text>{text}</Text>
        ),
    },
    {
      title: "Reserva",
      dataIndex: "price",
      key: "price",
      width: 130,
      render: (price, record) =>
        editingKey === record.id ? (
          <InputNumber
            prefix="$"
            value={editForm.price}
            onChange={(val) => setEditForm({ ...editForm, price: val })}
            style={{ width: "100%" }}
          />
        ) : (
          <Tag color="orange" style={priceTagStyle}>
            ${price}
          </Tag>
        ),
    },
    {
      title: "Precio total",
      dataIndex: "priceTotal",
      key: "priceTotal",
      width: 140,
      render: (priceTotal, record) =>
        editingKey === record.id ? (
          <InputNumber
            prefix="$"
            value={editForm.priceTotal}
            onChange={(val) => setEditForm({ ...editForm, priceTotal: val })}
            style={{ width: "100%" }}
          />
        ) : (
          <Tag color="green" style={priceTagStyle}>
            ${priceTotal}
          </Tag>
        ),
    },
    {
      title: "Duración",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      render: (duration, record) =>
        editingKey === record.id ? (
          <InputNumber
            min={15}
            step={15}
            value={editForm.duration}
            onChange={(val) => setEditForm({ ...editForm, duration: val })}
            addonAfter="min"
            style={{ width: "100%" }}
          />
        ) : (
          <Tag color="purple" style={priceTagStyle}>
            {duration} min
          </Tag>
        ),
    },
    {
      title: "Combinable",
      dataIndex: "combinable",
      key: "combinable",
      width: 140,
      render: (combinable, record) =>
        editingKey === record.id ? (
          <Select
            value={editForm.combinable}
            onChange={(value) =>
              setEditForm({
                ...editForm,
                combinable: value,
              })
            }
            options={[
              { value: true, label: "Sí" },
              { value: false, label: "No" },
            ]}
            style={{ width: "100%" }}
          />
        ) : (
          <Tag color={combinable ? "blue" : "default"} style={priceTagStyle}>
            {combinable ? "Sí" : "No"}
          </Tag>
        ),
    },
    {
      title: "Acciones",
      key: "action",
      width: 170,
      render: (_, record) => renderActions(record, "service"),
    },
  ];

  const baseExpandIcon = ({ expanded, onExpand, record }) => (
    <Button
      type="text"
      size="small"
      icon={expanded ? <MinusCircleOutlined /> : <PlusCircleOutlined />}
      onClick={(event) => onExpand(record, event)}
      style={{
        color: expanded ? "#94a3b8" : "#64748b",
      }}
    />
  );

  return (
    <div style={{ padding: 12 }}>
      <Card style={sectionCardStyle} bodyStyle={{ padding: 28 }}>
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <Text style={eyebrowStyle}>Catálogo del negocio</Text>
          <Title level={2} style={{ margin: "8px 0 4px" }}>
            Servicios
          </Title>
          <Text type="secondary">
            Organizá lo que ofrecés en tres niveles: áreas del salón, tipos de
            servicio y servicios reservables.
          </Text>

          <Row gutter={[16, 16]} style={{ marginTop: 22 }}>
            <Col xs={24} md={8}>
              <Card bordered={false} style={metricCardStyle}>
                <Statistic
                  title="Áreas del salón"
                  value={stats.categories}
                  prefix={<AppstoreOutlined style={{ color: "#3b82f6" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={metricCardStyle}>
                <Statistic
                  title="Tipos de servicio"
                  value={stats.subCategories}
                  prefix={
                    <FolderOpenOutlined style={{ color: "#10b981" }} />
                  }
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card bordered={false} style={metricCardStyle}>
                <Statistic
                  title="Servicios"
                  value={stats.services}
                  prefix={<ScissorOutlined style={{ color: "#8b5cf6" }} />}
                />
              </Card>
            </Col>
          </Row>

          <Card
            style={{ ...metricCardStyle, marginTop: 18 }}
            bodyStyle={{ padding: 20 }}
          >
            <Row justify="space-between" align="middle" gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Title level={4} style={{ margin: 0 }}>
                  Estructura del catálogo
                </Title>
                <Text type="secondary">
                  Abrí una fila para ver el siguiente nivel.
                </Text>
              </Col>
              <Col xs={24} lg={12} style={{ textAlign: "right" }}>
                <Space wrap>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setIsCatModalOpen(true)}
                  >
                    Nueva área
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setIsSubModalOpen(true)}
                  >
                    Nuevo tipo
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsSrvModalOpen(true)}
                  >
                    Nuevo servicio
                  </Button>
                </Space>
              </Col>
            </Row>

            <Alert
              style={{
                marginTop: 18,
                marginBottom: 18,
                borderRadius: 14,
                border: "1px solid #b6dcff",
              }}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message="Cómo administrar el catálogo"
              description="Usá la flecha de un área del salón para ver sus tipos de servicio y vuelve a expandir para ver los servicios. El precio de reserva es el anticipo; el precio total es el valor completo del servicio."
            />

            <Table
              loading={loading}
              columns={categoryColumns}
              dataSource={categories.map((category) => ({
                ...category,
                key: `cat-${category.id}`,
                id: `cat-${category.id}`,
              }))}
              pagination={false}
              rowKey="key"
              expandable={{
                expandIcon: baseExpandIcon,
                expandedRowRender: (category) => (
                  <Table
                    className={nestedTableClassName}
                    columns={subCategoryColumns}
                    dataSource={(category.SubCategories || []).map((subCategory) => ({
                      ...subCategory,
                      key: `sub-${subCategory.id}`,
                      id: `sub-${subCategory.id}`,
                    }))}
                    pagination={false}
                    size="middle"
                    rowKey="key"
                    expandable={{
                      expandIcon: baseExpandIcon,
                      expandedRowRender: (subCategory) => (
                        <Table
                          className={nestedTableClassName}
                          columns={serviceColumns}
                          dataSource={(subCategory.Services || []).map((service) => ({
                            ...service,
                            key: `srv-${service.id}`,
                            id: `srv-${service.id}`,
                          }))}
                          pagination={false}
                          size="small"
                          rowKey="key"
                        />
                      ),
                    }}
                  />
                ),
              }}
            />
          </Card>
        </div>
      </Card>

      <Modal
        title="Crear nueva área"
        open={isCatModalOpen}
        onCancel={() => setIsCatModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <CreateCategory
          onSuccess={() => {
            setIsCatModalOpen(false);
            fetchData();
          }}
        />
      </Modal>

      <Modal
        title="Crear nuevo tipo de servicio"
        open={isSubModalOpen}
        onCancel={() => setIsSubModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <CreateSubCategory
          onSuccess={() => {
            setIsSubModalOpen(false);
            fetchData();
          }}
        />
      </Modal>

      <Modal
        title="Crear nuevo servicio"
        open={isSrvModalOpen}
        onCancel={() => setIsSrvModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <CreateService
          onSuccess={() => {
            setIsSrvModalOpen(false);
            fetchData();
          }}
        />
      </Modal>

      <style>{`
        .${nestedTableClassName} .ant-table {
          background: #fcfdff;
        }
        .${nestedTableClassName} .ant-table-thead > tr > th {
          background: #fafbfd;
        }
      `}</style>
    </div>
  );
};

const eyebrowStyle = {
  color: "#3b82f6",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: 1,
  textTransform: "uppercase",
};

const priceTagStyle = {
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 10,
  paddingInline: 10,
  lineHeight: "24px",
};

export default AdminServices;
